import { useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { setAuthTokenGetter } from "@/lib/auth-fetch";
import { useChartStore } from "@/stores/chartStore";

// Local-only auth bypass — mirrors the API. When on, the API ignores auth, so
// the client must not make data requests wait on Clerk (which may be entirely
// unavailable in that mode). Keeps the bypass genuinely Clerk-independent.
const DEV_AUTH_BYPASS = ((import.meta as any).env as any).VITE_DEV_AUTH_BYPASS === "true";

// The user this browser last saw a signed-in session for. Persisted on its own
// key (not the chart-store) so a cross-reload account switch is detectable: on a
// shared browser, user B loading the app after user A must not inherit A's
// cached recents / identity / saved-charts list.
const LAST_USER_KEY = "chart-store:last-user";

const readLastUser = (): string | null => {
	try {
		return window.localStorage.getItem(LAST_USER_KEY);
	} catch {
		return null;
	}
};
const writeLastUser = (id: string | null) => {
	try {
		if (id) window.localStorage.setItem(LAST_USER_KEY, id);
		else window.localStorage.removeItem(LAST_USER_KEY);
	} catch {
		/* storage unavailable — nothing to persist */
	}
};

// Bridges Clerk's session token to the module-level authFetch helper so the
// non-component data hooks can attach an Authorization header, and clears any
// cached identity when the signed-in user changes. Mounted inside ClerkProvider
// and QueryClientProvider.
export function ClerkTokenBridge() {
	const { getToken, userId, isLoaded } = useAuth();
	const queryClient = useQueryClient();

	useEffect(() => {
		if (DEV_AUTH_BYPASS) {
			setAuthTokenGetter(() => Promise.resolve(null));
			return;
		}
		setAuthTokenGetter(() => getToken());
		return () => setAuthTokenGetter(null);
	}, [getToken]);

	useEffect(() => {
		// Skip while Clerk is still resolving, and entirely under the bypass
		// (userId is always null there — no real accounts to switch between).
		if (DEV_AUTH_BYPASS || !isLoaded) return;
		const current = userId ?? null;
		const last = readLastUser();
		// A real previous user that differs from the current one (a switch, or a
		// sign-out) — wipe every person-derived field (identity + recents + the
		// birth/present slots + the computed dial) and drop the per-user
		// saved-charts cache, so nothing from the last account survives a switch
		// that doesn't hard-reload the page.
		if (last !== null && last !== current) {
			useChartStore.getState().resetForAccountSwitch();
			queryClient.removeQueries({ queryKey: ["birthRecords"] });
		}
		writeLastUser(current);
	}, [userId, isLoaded, queryClient]);

	return null;
}

export default ClerkTokenBridge;
